import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { type LucideIcon } from "lucide-react";

interface KpiItem {
  icon: LucideIcon;
  labelKey: string;
  valueKey: string;
  color: string;
}

interface SolutionHeroI18nProps {
  translationPrefix: string;
  heroImage: string;
  floatingIcons: Array<{ icon: LucideIcon; position: string; delay: string; duration: string; color: "mint" | "orange" | "primary" }>;
  kpis: KpiItem[];
  barColor?: string;
}

const colorMap = {
  mint: { bg: "bg-chathero-mint/20", border: "border-chathero-mint/30", text: "text-chathero-mint" },
  orange: { bg: "bg-chathero-orange/20", border: "border-chathero-orange/30", text: "text-chathero-orange" },
  primary: { bg: "bg-primary/20", border: "border-primary/30", text: "text-primary" },
};

const SolutionHeroI18n = ({ translationPrefix, heroImage, floatingIcons, kpis, barColor = "bg-chathero-mint/60" }: SolutionHeroI18nProps) => {
  const { t, localizePath } = useLanguage();
  const prefix = `${translationPrefix}.hero`;

  return (
    <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-gradient-to-b from-primary/5 to-background overflow-hidden">
      <div className="container mx-auto px-4">
        <nav className="flex items-center gap-2 text-sm mb-8">
          <Link to={localizePath("/")} className="text-muted-foreground hover:text-foreground transition-colors">{t(`${prefix}.home`)}</Link>
          <span className="text-muted-foreground">/</span>
          <span className="text-muted-foreground">{t(`${prefix}.solutions`)}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-chathero-orange font-medium">{t(`${prefix}.breadcrumbCurrent`)}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-12 items-center">
          <div className="lg:col-span-2 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight animate-fade-in">
              {t(`${prefix}.title`)}
            </h1>
            <p className="text-xl md:text-2xl text-chathero-orange font-semibold mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              {t(`${prefix}.subtitle`)}
            </p>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl leading-relaxed animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {t(`${prefix}.description`)}
            </p>
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <Button size="lg" className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90 text-base px-8" asChild>
                <Link to={localizePath("/kontakt")}>{t(`${prefix}.ctaPrimary`)}</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base px-8" asChild>
                <a href="https://app.chathero.ai/register/essential" target="_blank" rel="noopener noreferrer">{t(`${prefix}.ctaSecondary`)}</a>
              </Button>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end animate-scale-in" style={{ animationDelay: "0.2s" }}>
            {floatingIcons.map((fi, i) => {
              const c = colorMap[fi.color];
              return (
                <div key={i} className={`absolute ${fi.position} z-10 animate-bounce`} style={{ animationDelay: fi.delay, animationDuration: fi.duration }}>
                  <div className={`w-${i === 0 ? 12 : i % 2 === 0 ? 10 : 11} h-${i === 0 ? 12 : i % 2 === 0 ? 10 : 11} rounded-2xl ${c.bg} backdrop-blur-sm border ${c.border} flex items-center justify-center shadow-lg`}>
                    <fi.icon className={`w-${i === 0 ? 6 : 5} h-${i === 0 ? 6 : 5} ${c.text}`} />
                  </div>
                </div>
              );
            })}

            <div className="relative w-full max-w-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-chathero-mint/20 to-primary/10 rounded-3xl blur-2xl" />
              <img src={heroImage} alt={t(`${prefix}.title`)} className="relative z-[1] w-full object-contain" width="400" height="500" loading="eager" fetchPriority="high" />

              <div className="absolute -right-10 sm:-right-16 bottom-[15%] z-20 animate-fade-in" style={{ animationDelay: "0.9s" }}>
                <div className="bg-background/90 backdrop-blur-md border border-border/60 rounded-2xl p-3 shadow-xl min-w-[160px]">
                  <p className="text-[10px] font-semibold text-primary mb-2">{t(`${prefix}.kpiTitle`)}</p>
                  <div className="space-y-2">
                    {kpis.map((kpi, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5">
                          <kpi.icon className={`w-3 h-3 ${kpi.color}`} />
                          <span className="text-[10px] text-muted-foreground">{t(`${prefix}.${kpi.labelKey}`)}</span>
                        </div>
                        <span className={`text-[11px] font-bold ${kpi.color}`}>{t(`${prefix}.${kpi.valueKey}`)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-end gap-0.5 mt-2 h-5">
                    {[40, 65, 45, 80, 60, 90, 75].map((h, i) => (
                      <div key={i} className={`flex-1 rounded-sm ${barColor}`} style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionHeroI18n;
